import React, { useState, useEffect } from "react";
import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Icon,
  ModalFooter,
  FormControl,
  FormLabel,
  Select,
  Input,
  HStack,
  IconButton,
  Box,
  useToast,
  Spinner,
} from "@chakra-ui/react";
import {
  FaPlus,
  FaTrash,
  FaDollarSign,
  FaRoute,
  FaClock,
  FaMapMarkerAlt,
  FaBus,
  FaMapSigns,
} from "react-icons/fa";
import axios from "axios";
import { BASE_URL } from "../../../Globalapi/Globalapi";

const RouteCreate = () => {
  const [startName, setStartName] = useState("");
  const [endName, setEndName] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pickupPoints, setPickupPoints] = useState([
    { point: "", time: "", dropTime: "", fees: "" },
  ]);
  const [selectedBus, setSelectedBus] = useState("");
  const [buses, setBuses] = useState([]);
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const schoolId = sessionStorage.getItem("school_id");
  const userId = sessionStorage.getItem("user_id");

  useEffect(() => {
    if (isModalOpen) {
      fetchBusDetails();
    }
  }, [isModalOpen]);

  const fetchBusDetails = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${BASE_URL}/admin/bus/vehicle/list_all_bus_details.php`,
        {
          params: { school_id: schoolId },
        }
      );
      if (response.data.success) {
        setBuses(response.data.data);
      } else {
        toast({
          title: "Error",
          description: response.data.message || "Failed to fetch bus details.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch bus details.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const handleAddPickupPoint = () => {
    setPickupPoints([
      ...pickupPoints,
      { point: "", time: "", dropTime: "", fees: "" },
    ]);
  };

  const handleRemovePickupPoint = (index) => {
    const updatedPoints = [...pickupPoints];
    updatedPoints.splice(index, 1);
    setPickupPoints(updatedPoints);
  };

  const handleChangePickupPoint = (index, field, value) => {
    const updatedPoints = [...pickupPoints];
    updatedPoints[index][field] = value;
    setPickupPoints(updatedPoints);
  };

  const handleSubmit = async () => {
    if (!startName || !endName || !selectedBus || pickupPoints.length === 0) {
      toast({
        title: "Validation Error",
        description: "Please fill all required fields.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    // Format the payload for the API
    const payload = {
      user_id: userId,
      school_id: schoolId,
      start_place: startName, // Changed from route_name
      end_place: endName, // Changed from route_name
      bus_id: selectedBus,
      bus_stops: pickupPoints.map((point) => ({
        stop_name: point.point, // Changed field names to match backend
        pickup_time: point.time, // Changed field names to match backend
        drop_time: point.dropTime, // Changed field names to match backend
        fee_amount: point.fees, // Changed field names to match backend
      })),
    };
    console.log("re", payload);

    try {
      const response = await fetch(
        `${BASE_URL}/admin/bus/bus_route/create_bus_route.php`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      const result = await response.json();

      if (response.ok && result.success) {
        toast({
          title: "Success",
          description: result.message,
          status: "success",
          duration: 4000,
          isClosable: true,
        });

        setStartName("");
        setEndName("");
        setSelectedBus("");
        setPickupPoints([{ point: "", time: "", dropTime: "", fees: "" }]);
        closeModal();
      } else {
        toast({
          title: "Error",
          description: result.message || "Failed to create bus route.",
          status: "error",
          duration: 4000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "An unexpected error occurred.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  return (
    <>
      <Button
        marginRight={2}
        leftIcon={<FaPlus />}
        colorScheme="teal"
        onClick={openModal}
        size={{ base: "xs", md: "md" }}
      >
        Route
      </Button>

      <Modal
        isCentered
        isOpen={isModalOpen}
        onClose={closeModal}
        size="lg"
        motionPreset="slideInBottom"
      >
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
        <ModalContent
          borderRadius="lg"
          boxShadow="xl"
          bg="linear-gradient(135deg, #e0f7f7, #f0ffff)"
          color="gray.700"
          p={6}
          maxW="700px"
          maxH="550px"
          overflowY="auto"
        >
          <ModalHeader
            fontSize="xl"
            fontWeight="bold"
            textAlign="center"
            color="teal.600"
          >
            <Icon as={FaPlus} boxSize={5} mr={2} />
            Add Route
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody
            overflowY="auto"
            px={4}
            css={{
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                background: "rgba(255, 255, 255, 0.2)",
                borderRadius: "10px",
                backdropFilter: "blur(8px)",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "rgba(173, 216, 230, 0.6)",
                borderRadius: "10px",
                backdropFilter: "blur(8px)",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "rgba(173, 216, 230, 0.9)",
              },
            }}
          >
            <HStack mb={4} spacing={4}>
              <FormControl>
                <FormLabel>
                  {" "}
                  <Icon as={FaMapMarkerAlt} mr={1} color="teal.500" />
                  Journey Starts From
                </FormLabel>
                <Input
                  bg="white"
                  _hover={{ borderColor: "teal.300" }}
                  placeholder="Enter start name"
                  value={startName}
                  onChange={(e) => setStartName(e.target.value)}
                />
              </FormControl>

              <FormControl>
                <FormLabel>
                  {" "}
                  <Icon as={FaMapSigns} mr={1} color="teal.500" />
                  Destination
                </FormLabel>
                <Input
                  bg="white"
                  _hover={{ borderColor: "teal.300" }}
                  placeholder="Enter end name"
                  value={endName}
                  onChange={(e) => setEndName(e.target.value)}
                />
              </FormControl>
            </HStack>

            <FormControl mb={4}>
              <FormLabel>
                {" "}
                <Icon as={FaBus} mr={1} color="teal.500" />
                Select Bus
              </FormLabel>
              {loading ? (
                <Spinner size="sm" />
              ) : (
                <Select
                  bg="white"
                  _hover={{ borderColor: "teal.300" }}
                  placeholder="Select a bus"
                  value={selectedBus}
                  onChange={(e) => setSelectedBus(e.target.value)}
                >
                  {buses.map((bus) => (
                    <option key={bus.id} value={bus.id}>
                      {bus.bus_no}
                    </option>
                  ))}
                </Select>
              )}
            </FormControl>

            <Box>
              <FormLabel>
                {" "}
                <Icon as={FaMapSigns} mr={1} color="teal.500" />
                Pickup Points
              </FormLabel>
              <HStack mb={2} spacing={4}>
                <Box flex="3" textAlign="center" fontSize="xs">
                  <Icon as={FaMapSigns} mr={1} color="teal.500" /> Check Point
                </Box>
                <Box flex="3" textAlign="center" fontSize="xs">
                  <Icon as={FaClock} mr={1} color="teal.500" /> Pickup Time
                </Box>
                <Box flex="3" textAlign="center" fontSize="xs">
                  <Icon as={FaClock} mr={1} color="teal.500" /> Drop Time
                </Box>
                <Box flex="3" textAlign="center" fontSize="xs">
                  <Icon as={FaDollarSign} mr={1} color="teal.500" /> Check Point
                  Fees
                </Box>
                <Box flex="0.5" />
              </HStack>
              {pickupPoints.map((pickup, index) => (
                <HStack key={index} mb={4} spacing={4}>
                  <Input
                    bg="white"
                    _hover={{ borderColor: "teal.300" }}
                    placeholder="Pickup Point"
                    value={pickup.point}
                    onChange={(e) =>
                      handleChangePickupPoint(index, "point", e.target.value)
                    }
                  />

                  <Input
                    bg="white"
                    _hover={{ borderColor: "teal.300" }}
                    type="time"
                    value={pickup.time}
                    onChange={(e) =>
                      handleChangePickupPoint(index, "time", e.target.value)
                    }
                  />

                  <Input
                    bg="white"
                    _hover={{ borderColor: "teal.300" }}
                    type="time"
                    value={pickup.dropTime}
                    onChange={(e) =>
                      handleChangePickupPoint(index, "dropTime", e.target.value)
                    }
                  />

                  <Input
                    bg="white"
                    _hover={{ borderColor: "teal.300" }}
                    type="number"
                    placeholder="Fees"
                    value={pickup.fees}
                    onChange={(e) =>
                      handleChangePickupPoint(index, "fees", e.target.value)
                    }
                  />

                  <IconButton
                    icon={<FaTrash />}
                    colorScheme="red"
                    size="xs"
                    onClick={() => handleRemovePickupPoint(index)}
                    aria-label="Remove Pickup Point"
                  />
                </HStack>
              ))}
              <Button
                size="sm"
                colorScheme="teal"
                leftIcon={<FaPlus />}
                onClick={handleAddPickupPoint}
              >
                Add Pickup
              </Button>
            </Box>
          </ModalBody>
          <ModalFooter>
            {/* <Button variant="ghost" onClick={closeModal} mr={3}>
              Cancel
            </Button> */}
            <Button
              bgGradient="linear(to-r, teal.400, teal.600)"
              color="white"
              _hover={{ bgGradient: "linear(to-r, teal.500, teal.700)" }}
              onClick={handleSubmit}
            >
              Create
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default RouteCreate;
